import configUwmGetters from '../../configs/getters/configUwmGetters';
import userGroupNames from '../../consts/auth/userGroupNames';
import DEVICE_TYPES_DATA from '../../consts/device/deviceTypesData';
import IAP_DEVICE_MODELS from '../../consts/iap/iapDeviceModels';
import UWM_FEATURE_ENTRIES from '../../consts/uwm/uwmFeatureEntries';
import {selectUserChannel, selectUserGroups, selectUserRoles} from '../../state/selectors/auth';
import {selectCurrentConnectionType, selectCurrentDeviceType} from '../../state/selectors/iotDevices';
import {getState} from '../../state/store';
import {getConfig} from '../config/configService';
import channelsConfig from './channelsConfig';
import groupsConfig from './groupsConfig';
import rolesConfig from './rolesConfig';

const getDeviceFeatureCode = (deviceType) => {
    const featureDeviceType = deviceType || selectCurrentDeviceType(getState());

    if (!featureDeviceType) return null;

    return DEVICE_TYPES_DATA[featureDeviceType]?.deviceFeatureCode || null;
};

// One family spans models with different setup transports (MERCURY_W / MERCURY_L of ILUMA_I2);
// a transport no model is set up over — Sidewalk, BLE — falls back to the whole family.
const getDeviceModels = (deviceType, connectionType) => {
    const deviceFeatureCode = getDeviceFeatureCode(deviceType);

    if (!deviceFeatureCode) return [];

    const familyModels = Object.entries(IAP_DEVICE_MODELS).filter(
        ([, modelData]) => modelData.deviceFeatureCode === deviceFeatureCode
    );
    const transportModels = familyModels.filter(([, modelData]) => modelData.connectionType === connectionType);

    return (transportModels.length ? transportModels : familyModels).map(([deviceModel]) => deviceModel);
};

const getDeviceFeatureValue = (featureCode, deviceType, connectionType) => {
    // Presence in `deviceFeatures` is what makes a code device-scoped; the rest live in `settings`.
    const enabledDeviceModels = getConfig().deviceFeatures?.[featureCode];

    if (!enabledDeviceModels) return undefined;

    const featureConnectionType = connectionType || selectCurrentConnectionType(getState());
    const deviceModels = getDeviceModels(deviceType, featureConnectionType);

    if (!deviceModels.length) return undefined;

    return deviceModels.some((deviceModel) => enabledDeviceModels.includes(deviceModel));
};

const getIsFeatureAvailableByMarket = (featureCode, deviceType, connectionType) => {
    const deviceFeatureValue = getDeviceFeatureValue(featureCode, deviceType, connectionType);

    if (typeof deviceFeatureValue === 'boolean') return deviceFeatureValue;

    return getConfig().settings?.[featureCode];
};

const getIsFeatureAvailableByRoles = (featureCode) => {
    const userRoles = selectUserRoles(getState());

    return userRoles.some((role) => rolesConfig[role]?.includes(featureCode));
};

// An entry gates a feature outright (`null`) or only over the connection types it lists; unknown = gated.
const getIsFeatureGated = (gatedFeatures, featureCode, connectionType) => {
    if (!gatedFeatures || !(featureCode in gatedFeatures)) return false;

    const gatedConnectionTypes = gatedFeatures[featureCode];

    if (!gatedConnectionTypes || !connectionType) return true;

    return gatedConnectionTypes.includes(connectionType);
};

const getIsFeatureAvailableByChannel = (featureCode, connectionType) => {
    const userChannel = selectUserChannel(getState());

    const isGatedByAnyChannel = Object.values(channelsConfig).some((channelFeatures) =>
        getIsFeatureGated(channelFeatures, featureCode, connectionType)
    );

    // If no channel mentions the feature, it's available to everyone
    if (!isGatedByAnyChannel) return true;

    return getIsFeatureGated(channelsConfig[userChannel], featureCode, connectionType);
};

const getIsFeatureAvailableByGroups = (featureCode, connectionType) => {
    const userGroups = selectUserGroups(getState());

    const restrictedGroups = Object.entries(groupsConfig)
        .filter(([, groupFeatures]) => getIsFeatureGated(groupFeatures, featureCode, connectionType))
        .map(([group]) => group);

    // If the feature isn't mentioned in groupsConfig, it's available to everyone
    if (!restrictedGroups.length) return true;

    // NOTE: auto-detect "network-only" features (configured only by CELLULAR/WI_FI) and,
    // if user has neither of those groups, allow them.
    const isNetworkOnly = restrictedGroups.every((g) => g === userGroupNames.CELLULAR || g === userGroupNames.WI_FI);

    if (isNetworkOnly) {
        const hasCellular = userGroups.includes(userGroupNames.CELLULAR);
        const hasWifi = userGroups.includes(userGroupNames.WI_FI);

        if (!hasCellular && !hasWifi) return true;
    }

    // Feature is restricted allow only if the user belongs to at least one required group
    return restrictedGroups.some((g) => userGroups.includes(g));
};

const getIsFeatureAvailableByUwm = (featureCode, deviceType) => {
    const featureEntries = UWM_FEATURE_ENTRIES[featureCode];

    if (!featureEntries) return true;

    const deviceFeatureCode = getDeviceFeatureCode(deviceType);
    const entry = deviceFeatureCode && featureEntries[deviceFeatureCode];

    if (!entry) return true;

    return configUwmGetters.getIsUwmEntryEnabled(entry);
};

const getIsFeatureEnabled = (featureCode, deviceType, connectionType) => {
    const isFeatureAvailableByMarket = getIsFeatureAvailableByMarket(featureCode, deviceType, connectionType);
    const isFeatureAvailableByRoles = getIsFeatureAvailableByRoles(featureCode);
    const isFeatureAvailableByGroups = getIsFeatureAvailableByGroups(featureCode, connectionType);
    const isFeatureAvailableByChannel = getIsFeatureAvailableByChannel(featureCode, connectionType);
    const isFeatureAvailableByUwm = getIsFeatureAvailableByUwm(featureCode, deviceType);

    return (
        isFeatureAvailableByMarket &&
        isFeatureAvailableByRoles &&
        isFeatureAvailableByGroups &&
        isFeatureAvailableByChannel &&
        isFeatureAvailableByUwm
    );
};

export default {
    getIsFeatureEnabled,
};
